import { CreateBookingDTO } from '@/clients/booking'

type LocalHandlingProduct = { code: string; name?: string }
type LocalHandlingService = { products: LocalHandlingProduct[] }

// Helper-only fields used by the UI layer; must never be sent to the API.
export type LocalHandlingHelpers = {
  originLocalHandlingService?: LocalHandlingService | null
  mainCarriageDocumentType?: LocalHandlingService | undefined
}

const dedupeByCode = (products: LocalHandlingProduct[]): LocalHandlingProduct[] => {
  const seen = new Set<string>()
  const unique: LocalHandlingProduct[] = []
  for (const product of products) {
    if (!product?.code) continue
    if (seen.has(product.code)) continue
    seen.add(product.code)
    unique.push(product)
  }
  return unique
}

const inferMainCarriageDocumentTypeFromBooking = (booking?: {
  originLocalHandlingService?: LocalHandlingService | null
}): LocalHandlingService | undefined => {
  const products = booking?.originLocalHandlingService?.products ?? []

  // Best-effort inference: if booking already contains known doc-type product codes,
  // expose them as the derived document-type helper.
  const MAIN_CARRIAGE_DOC_CODES = new Set(['COUO', 'VCO'])
  const docProducts = products.filter((p) => (p?.code ? MAIN_CARRIAGE_DOC_CODES.has(p.code) : false))
  return docProducts.length > 0 ? { products: docProducts } : undefined
}

/**
 * Normalizes origin local handling services to the backend shape.
 *
 * Behavior (as required):
 * - Strips helper field `mainCarriageDocumentType` from the outgoing payload.
 * - If both helper inputs are `undefined` and no booking is provided, returns payload untouched.
 * - Otherwise, builds `originLocalHandlingService.products` by merging:
 *   - payload.originLocalHandlingService.products
 *   - payload.mainCarriageDocumentType.products
 *   - dedupes by product code
 * - If merged list ends empty, sets originLocalHandlingService = null.
 * - In update mode, if booking is provided: any helper input that is `undefined` is derived from booking.
 */
export const sanitizeOriginLocalHandlingService = <TPayload extends CreateBookingDTO & Partial<LocalHandlingHelpers>>(
  payload: TPayload,
  booking?: { originLocalHandlingService?: LocalHandlingService | null }
): CreateBookingDTO => {
  const hasAllUndefinedInputs =
    payload.originLocalHandlingService === undefined && payload.mainCarriageDocumentType === undefined

  // In create mode (no booking), when helpers are untouched, return payload as-is.
  if (hasAllUndefinedInputs && !booking) {
    return payload
  }

  const resolvedOrigin =
    payload.originLocalHandlingService !== undefined
      ? payload.originLocalHandlingService
      : (booking?.originLocalHandlingService ?? undefined)

  const resolvedDoc =
    payload.mainCarriageDocumentType !== undefined
      ? payload.mainCarriageDocumentType
      : inferMainCarriageDocumentTypeFromBooking(booking)

  const originProducts = resolvedOrigin?.products ?? []
  const docProducts = resolvedDoc?.products ?? []

  let products = [...originProducts, ...docProducts]

  products = dedupeByCode(products)

  const normalizedOrigin = products.length === 0 ? null : { products }

  const { mainCarriageDocumentType: _strip1, ...rest } = payload

  const nextPayload = {
    ...rest,
    originLocalHandlingService: normalizedOrigin,
  }

  // Ensure we return a strict CreateBookingDTO (helpers stripped).
  return nextPayload as CreateBookingDTO
}
